'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, MessageSquare, Zap, Shield, Users, ArrowRight, Loader2, Check } from 'lucide-react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';


type LoginFormData = {
  email: string;
  password: string;
  remember: boolean;
};

type SignupFormData = {
  name: string;
  email: string;
  company: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
};

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupFormData>({ defaultValues: { name: '', email: '', company: '', password: '', confirmPassword: '', terms: false } });

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError(null);
    try {
      await signIn(data.email, data.password);
      router.push('/agent-dashboard');
      router.refresh();
    } catch (err: any) {
      setLoginError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSignupSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match.' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.name,
        company: data.company,
        role: 'agent',
      });
      setSignupSuccess(true);
      router.push('/agent-dashboard');
      router.refresh();
    } catch (err: any) {
      signupForm.setError('email', { message: err?.message || 'Sign up failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[520px] bg-[#1E3A5F] text-white p-10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <AppLogo size={36} />
          <span className="font-bold text-xl tracking-tight">ChatDesk</span>
        </div>

        <div>
          <h1 className="text-4xl font-800 leading-tight mb-4">
            Support at the speed<br />of conversation.
          </h1>
          <p className="text-blue-200 text-[15px] leading-relaxed mb-10">
            Real-time chat, intelligent automation, and deep visitor insights — all in one platform built for high-performance support teams.
          </p>

          <div className="space-y-4">
            {[
              { id: 'feat-inbox', icon: <MessageSquare size={18} />, title: 'Unified Team Inbox', desc: 'All channels, one workspace' },
              { id: 'feat-bot', icon: <Zap size={18} />, title: 'Chatbot Automation', desc: 'Deflect 40% of tickets automatically' },
              { id: 'feat-track', icon: <Shield size={18} />, title: 'Live Visitor Tracking', desc: 'Proactive outreach at the right moment' },
              { id: 'feat-roles', icon: <Users size={18} />, title: 'Role-Based Access', desc: 'Admin, Supervisor, and Agent tiers' },
            ].map((f) => (
              <div key={f.id} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {f.icon}
                </div>
                <div>
                  <p className="font-600 text-[14px]">{f.title}</p>
                  <p className="text-blue-200 text-[13px]">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-blue-300 text-[12px]">© 2026 ChatDesk Inc. · Privacy · Terms</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background overflow-y-auto">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-foreground">ChatDesk</span>
          </div>

          {/* Tabs */}
          <div className="flex rounded-lg border border-border p-1 bg-muted mb-6">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => { setTab(t); setLoginError(null); setSignupSuccess(false); }}
                className={`flex-1 py-2 text-[13.5px] font-[600] rounded-md transition-all duration-150 ${
                  tab === t ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Login form */}
          {tab === 'login' && (
            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4 fade-in">
              <div>
                <label className="block text-[13px] font-[600] text-foreground mb-1.5">
                  Work Email
                </label>
                <input
                  type="email"
                  {...loginForm.register('email', { required: 'Email is required.' })}
                  className="w-full px-3 py-2.5 text-[13.5px] border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
                  placeholder="you@company.com"
                />
                {loginForm.formState.errors.email && (
                  <p className="text-[12px] text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[13px] font-[600] text-foreground">Password</label>
                  <button type="button" className="text-[12px] text-primary hover:underline">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required.' })}
                    className="w-full px-3 py-2.5 pr-10 text-[13.5px] border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-[12px] text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  {...loginForm.register('remember')}
                  className="w-4 h-4 rounded border-border accent-primary"
                />
                <label htmlFor="remember" className="text-[13px] text-muted-foreground">
                  Remember me for 30 days
                </label>
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                  <p className="text-[12.5px] text-red-600">{loginError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white py-2.5 rounded-md text-[14px] font-[600] hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-70"
              >
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> Signing in…</>
                ) : (
                  <>Sign In <ArrowRight size={15} /></>
                )}
              </button>
            </form>
          )}

          {/* Signup form */}
          {tab === 'signup' && !signupSuccess && (
            <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4 fade-in">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-[13px] font-[600] text-foreground mb-1.5">Full Name</label>
                  <input
                    type="text"
                    {...signupForm.register('name', { required: 'Name is required.' })}
                    className="w-full px-3 py-2.5 text-[13.5px] border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
                    placeholder="Your full name"
                  />
                  {signupForm.formState.errors.name && (
                    <p className="text-[12px] text-red-500 mt-1">{signupForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-[13px] font-[600] text-foreground mb-1.5">Work Email</label>
                  <input
                    type="email"
                    {...signupForm.register('email', { required: 'Email is required.' })}
                    className="w-full px-3 py-2.5 text-[13.5px] border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
                    placeholder="you@company.com"
                  />
                  {signupForm.formState.errors.email && (
                    <p className="text-[12px] text-red-500 mt-1">{signupForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-[13px] font-[600] text-foreground mb-1.5">Company Name</label>
                  <input
                    type="text"
                    {...signupForm.register('company', { required: 'Company is required.' })}
                    className="w-full px-3 py-2.5 text-[13.5px] border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
                    placeholder="Acme Corp"
                  />
                  {signupForm.formState.errors.company && (
                    <p className="text-[12px] text-red-500 mt-1">{signupForm.formState.errors.company.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] font-[600] text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...signupForm.register('password', { required: 'Password is required.', minLength: { value: 8, message: 'Min 8 characters.' } })}
                      className="w-full px-3 py-2.5 pr-9 text-[13.5px] border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
                      placeholder="••••••••"
                    />
                    <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="text-[12px] text-red-500 mt-1">{signupForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] font-[600] text-foreground mb-1.5">Confirm</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...signupForm.register('confirmPassword', { required: 'Required.' })}
                      className="w-full px-3 py-2.5 pr-9 text-[13.5px] border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
                      placeholder="••••••••"
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(s => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
                      {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.confirmPassword && (
                    <p className="text-[12px] text-red-500 mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  {...signupForm.register('terms', { required: 'You must accept the terms.' })}
                  className="w-4 h-4 mt-0.5 rounded border-border accent-primary"
                />
                <label htmlFor="terms" className="text-[12.5px] text-muted-foreground leading-relaxed">
                  I agree to ChatDesk&apos;s{' '}
                  <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
                  {' '}and{' '}
                  <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
                </label>
              </div>
              {signupForm.formState.errors.terms && (
                <p className="text-[12px] text-red-500 -mt-2">{signupForm.formState.errors.terms.message}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white py-2.5 rounded-md text-[14px] font-[600] hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-70"
              >
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> Creating account…</>
                ) : (
                  <>Create Account <ArrowRight size={15} /></>
                )}
              </button>
            </form>
          )}

          {tab === 'signup' && signupSuccess && (
            <div className="text-center py-8 fade-in">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check size={24} className="text-green-600" />
              </div>
              <h2 className="text-[18px] font-700 text-foreground mb-2">Account created!</h2>
              <p className="text-[13.5px] text-muted-foreground mb-6">
                Your account is ready. Redirecting you to the dashboard…
              </p>
              <button
                onClick={() => { setTab('login'); setSignupSuccess(false); }}
                className="text-[13.5px] font-[600] text-primary hover:underline"
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}